import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Send, ShoppingBag } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RETAIL_BASE = `${BACKEND_URL}/api/retail/public`;

// Phase 3M — extracted verbatim from RetailCustomer.jsx::ChatPanel.
// Phase 3L.1 will retarget this to open the full assistant route. Until
// then this component MUST stay identical to the current side-pane
// behaviour and testids.
export default function ChatEntryPanel({ slug, sessionId, tenant, onClose, onStartOrder }) {
  const [messages, setMessages] = useState(() => ([
    { role: "assistant", text: tenant?.assistant_greeting || "Hello! Ask me anything about the menu — when you're ready, tap Start Sales Order." },
  ]));
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${RETAIL_BASE}/${slug}/chat`, {
        message: text, session_id: sessionId,
      });
      const reply = data?.answer || data?.message || data?.reply || "Sorry, I didn't catch that.";
      setMessages((m) => [...m, { role: "assistant", text: reply }]);
    } catch (_e) {
      setMessages((m) => [...m, { role: "assistant", text: "I'm having trouble right now. You can still tap Start Sales Order below." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-testid="retail-chat-panel">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}/>
      <div className="relative z-10 w-full sm:w-[440px] h-full bg-[#0a1a0e] text-emerald-50 flex flex-col">
        <div className="px-4 py-3 border-b border-emerald-900/40 flex items-center gap-3">
          <button onClick={onClose} aria-label="Close chat" data-testid="retail-chat-close"><X className="w-5 h-5"/></button>
          <div className="font-serif text-lg flex-1">Chat with us</div>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
          {messages.map((m, i) => (
            <div key={i} className={"text-sm rounded-lg px-3 py-2 max-w-[85%] " + (m.role === "user" ? "ml-auto bg-orange-500 text-white" : "bg-emerald-900/50 text-emerald-50")}>
              {m.text}
            </div>
          ))}
          {sending && (
            <div className="text-xs text-emerald-200/60 px-3">Typing…</div>
          )}
        </div>
        <form onSubmit={send} className="px-3 py-3 border-t border-emerald-900/40 flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your question…"
            data-testid="retail-chat-input"
            className="flex-1 h-10 rounded-md border border-emerald-900/40 bg-[#102718] px-3 text-sm text-emerald-50 placeholder:text-emerald-200/40 focus:outline-none focus:ring-1 focus:ring-orange-400"
          />
          <button
            type="submit"
            disabled={!input.trim() || sending}
            data-testid="retail-chat-send"
            className="h-10 w-10 grid place-items-center rounded-md bg-orange-500 text-white hover:bg-orange-600 disabled:bg-orange-500/40"
            aria-label="Send"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
        <div className="px-3 pb-3">
          <button
            type="button"
            onClick={onStartOrder}
            data-testid="retail-chat-start-order"
            className="w-full h-11 rounded-md bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 inline-flex items-center justify-center gap-2"
          >
            <ShoppingBag className="w-4 h-4" /> Start Sales Order
          </button>
        </div>
      </div>
    </div>
  );
}
